/**
 * 时间线验证配置
 * 为新 ValidationEngine 提供验证规则
 */

import type { ValidationConfig } from '../../../core/dataProcessor/types';

// 🆕 为新 ValidationEngine 导出的配置
export const timeLineValidationConfig: ValidationConfig = {
  fields: [
    { field: 'time', type: ['date', 'string'], required: true },
    { field: 'title', type: 'string', required: true },
    { field: 'label', type: 'string', required: true },
    { field: 'importance', type: ['number', 'null'], required: false },
    { field: 'url', type: 'string', required: false }
  ],
  businessRules: [
    {
      name: 'timeline-enhanced-date-validation',
      validator: (data, layer) => {
        if (!Array.isArray(data)) {
          return false;
        }

        const { encoding } = layer;
        return data.every((item: Record<string, unknown>) => {
          const timeValue = item[encoding?.time as string];
          if (!timeValue) {
            return false;
          }

          // 支持多种日期格式
          const dateFormats = [
            // YYYY-MM-DD
            /^\d{4}-\d{2}-\d{2}$/,
            // YYYY-MM-DD HH:mm
            /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}$/,
            // YYYY-MM-DD HH:mm:ss
            /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/
          ];

          // 如果是字符串，检查格式
          if (typeof timeValue === 'string') {
            const matchesFormat = dateFormats.some(format => format.test(timeValue));
            if (!matchesFormat) {
              return false;
            }

            // 验证是否为有效日期
            const date = new Date(timeValue);
            return !Number.isNaN(date.getTime());
          }

          // 如果是 Date 对象或时间戳
          if (typeof timeValue === 'number' || timeValue instanceof Date) {
            const date = new Date(timeValue);
            return !Number.isNaN(date.getTime());
          }

          return false;
        });
      },
      message: 'Timeline contains invalid date/time format',
      suggestion: 'Use valid date formats: YYYY-MM-DD, YYYY-MM-DD HH:mm, or YYYY-MM-DD HH:mm:ss',
      severity: 'error'
    },
    {
      name: 'timeline-importance-range',
      validator: (data, layer) => {
        if (!Array.isArray(data)) {
          return false;
        }

        const { encoding } = layer;
        return data.every((item: Record<string, unknown>) => {
          const importance = item[encoding?.importance as string];
          // importance 字段现在是可选的，允许 null 值
          if (importance === null || importance === undefined) {
            return true;
          }
          return typeof importance === 'number' && importance >= 1 && importance <= 5;
        });
      },
      message: 'Timeline importance should be between 1 and 5',
      suggestion: 'Set importance values between 1 (low) and 5 (high) for proper timeline display',
      severity: 'warning'
    },
    {
      name: 'timeline-title-not-empty',
      validator: (data, layer) => {
        if (!Array.isArray(data)) {
          return false;
        }

        const { encoding } = layer;
        return data.every((item: Record<string, unknown>) => {
          const title = item[encoding?.title as string];
          return title && typeof title === 'string' && title.trim().length > 0;
        });
      },
      message: 'Timeline title cannot be empty',
      suggestion: 'Ensure all timeline events have meaningful titles',
      severity: 'error'
    },
    {
      name: 'timeline-label-not-empty',
      validator: (data, layer) => {
        if (!Array.isArray(data)) {
          return false;
        }

        const { encoding } = layer;
        return data.every((item: Record<string, unknown>) => {
          const label = item[encoding?.label as string];
          return label && typeof label === 'string' && label.trim().length > 0;
        });
      },
      message: 'Timeline label cannot be empty',
      suggestion: 'Ensure all timeline events have meaningful labels',
      severity: 'error'
    },
    {
      name: 'timeline-url-format',
      validator: (data, layer) => {
        if (!Array.isArray(data)) {
          return false;
        }

        const { encoding } = layer;
        if (!encoding?.url) {
          return true;
        }

        return data.every((item: Record<string, unknown>) => {
          const url = item[encoding.url as string];
          // URL is optional
          if (!url) {
            return true;
          }

          return (
            typeof url === 'string' && (url.startsWith('http://') || url.startsWith('https://'))
          );
        });
      },
      message: 'Timeline URL format is invalid',
      suggestion: 'Ensure URLs start with http:// or https://',
      severity: 'warning'
    },
    {
      name: 'timeline-sufficient-events',
      validator: data => {
        if (!Array.isArray(data)) {
          return false;
        }
        return data.length >= 2;
      },
      message: 'Timeline works best with at least 2 events',
      suggestion: 'Provide at least 2 timeline events for meaningful visualization',
      severity: 'warning'
    },
    {
      name: 'timeline-optimal-event-count',
      validator: data => {
        if (!Array.isArray(data)) {
          return false;
        }
        return data.length <= 50;
      },
      message: 'Timeline works best with 50 or fewer events',
      suggestion: 'Consider limiting to 50 events for better performance and readability',
      severity: 'warning'
    }
  ]
};
